/**
 * The 「ヘルプ」 button on every screen and its chat sheet (modelled on the Zepp
 * app's AIサポート). The AI helpdesk answers at once; engineers reply into the
 * same case, so the open sheet re-reads the case every 5 s, and a closed one
 * every 20 s (only while a case is open) to light the unread dot.
 *
 * Message bodies are rendered as text, never markup. If the support API is not
 * deployed (404/503) nothing is shown at all: the vending journey never
 * depends on this widget.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { ActionKeys, ApiError, NetworkError } from "../api";
import { Spinner } from "../components";
import { useI18n } from "../i18n";
import { store } from "../storage";
import {
  getCase,
  getOpenCase,
  isCaseGone,
  isSupportUnavailable,
  latestReplySeq,
  resolveCase,
  roleLabelKey,
  sendMessage,
  supportErrorKey,
  type SendRequest,
  type SupportCase,
  type SupportRole,
} from "./supportApi";
import { buildClientContext, reportRoute } from "./supportContext";

const OPEN_POLL_MS = 5_000;
const CLOSED_POLL_MS = 20_000;
const MAX_MESSAGE = 1000;

// ------------------------------------------------------- persisted state

function seenSeq(caseId: string): number {
  const raw = store.get("support_seen");
  const at = raw ? raw.lastIndexOf(":") : -1;
  if (!raw || at < 0 || raw.slice(0, at) !== caseId) return -1;
  const n = Number(raw.slice(at + 1));
  return Number.isInteger(n) ? n : -1;
}

function markSeen(c: SupportCase): void {
  const seq = latestReplySeq(c);
  if (seq >= 0) store.set("support_seen", `${c.case_id}:${seq}`);
}

function hasUnread(c: SupportCase): boolean {
  return latestReplySeq(c) > seenSeq(c.case_id);
}

/** The remembered case, else the caller's latest open one. */
async function loadCase(signal?: AbortSignal): Promise<SupportCase | null> {
  const saved = store.get("support_case");
  if (saved) {
    try {
      return await getCase(saved, signal);
    } catch (e) {
      if (!isCaseGone(e)) throw e;
      store.remove("support_case");
    }
  }
  return getOpenCase(signal);
}

// ------------------------------------------------ keep clear of the bars

/**
 * Height of the screen's sticky action bar (it already includes the safe-area
 * inset), so the button floats above 「支払い」 instead of covering it.
 */
function useActionBarHeight(): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    let bar: Element | null = null;
    let resize: ResizeObserver | null = null;
    const attach = () => {
      const next = document.querySelector(".aseed-sticky-actions");
      if (next === bar) return;
      resize?.disconnect();
      resize = null;
      bar = next;
      if (!next) {
        setHeight(0);
        return;
      }
      const measure = () => setHeight(Math.round(next.getBoundingClientRect().height));
      measure();
      if (typeof ResizeObserver !== "undefined") {
        resize = new ResizeObserver(measure);
        resize.observe(next);
      }
    };
    attach();
    const mutations = new MutationObserver(attach);
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      mutations.disconnect();
      resize?.disconnect();
    };
  }, []);
  return height;
}

// ------------------------------------------------------------- the widget

type Busy = null | "send" | "resolve";

export function SupportChat() {
  const { t, locale } = useI18n();
  const { pathname } = useLocation();
  const [available, setAvailable] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState<SupportCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unread, setUnread] = useState(false);
  const keys = useRef(new ActionKeys());
  const openRef = useRef(false);
  const busyRef = useRef(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lift = useActionBarHeight();

  useEffect(() => {
    reportRoute(pathname);
  }, [pathname]);

  /** Take a case read from the server. A poll never rolls back a newer send. */
  const adopt = useCallback((c: SupportCase | null) => {
    setChat((prev) =>
      c && prev && prev.case_id === c.case_id && prev.messages.length > c.messages.length ? prev : c,
    );
    if (!c) return;
    if (c.status === "open") store.set("support_case", c.case_id);
    if (openRef.current) {
      markSeen(c);
      setUnread(false);
    } else {
      setUnread(hasUnread(c));
    }
  }, []);

  // Is the support API there at all? Also restores the conversation.
  useEffect(() => {
    const ctrl = new AbortController();
    loadCase(ctrl.signal)
      .then((c) => {
        setAvailable(true);
        adopt(c);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setAvailable(!isSupportUnavailable(e));
      });
    return () => ctrl.abort();
  }, [adopt]);

  const liveCaseId = chat?.status === "open" ? chat.case_id : null;

  // 5 s while the sheet is open (engineer replies), 20 s while closed (unread dot).
  useEffect(() => {
    if (!available || !liveCaseId) return;
    let ctrl: AbortController | null = null;
    const timer = setInterval(
      () => {
        if (busyRef.current || document.visibilityState === "hidden") return;
        ctrl?.abort();
        const mine = new AbortController();
        ctrl = mine;
        getCase(liveCaseId, mine.signal)
          .then(adopt)
          .catch((e) => {
            if (mine.signal.aborted || !isCaseGone(e)) return;
            store.remove("support_case");
            setChat(null);
          });
      },
      open ? OPEN_POLL_MS : CLOSED_POLL_MS,
    );
    return () => {
      clearInterval(timer);
      ctrl?.abort();
    };
  }, [available, liveCaseId, open, adopt]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open, available]);

  // Keep the newest message in view.
  const count = chat?.messages.length ?? 0;
  useEffect(() => {
    const el = listRef.current;
    if (open && el) el.scrollTop = el.scrollHeight;
  }, [open, count, pending, notice]);

  function show() {
    openRef.current = true;
    setOpen(true);
    setUnread(false);
    setNotice(null);
    if (chat) markSeen(chat);
    setLoading(true);
    loadCase()
      .then(adopt)
      .catch((e) => {
        if (!isSupportUnavailable(e)) setNotice(supportErrorKey(e));
      })
      .finally(() => setLoading(false));
  }

  function hide() {
    openRef.current = false;
    setOpen(false);
  }

  async function send() {
    const text = draft.trim();
    if (!text) {
      setNotice("support.needText");
      return;
    }
    // One key per question, reused only when retrying it after a dropped connection.
    const action = `send:${text}`;
    const request: SendRequest = { message: text, locale, context: buildClientContext() };
    if (chat?.status === "open") request.case_id = chat.case_id;
    busyRef.current = true;
    setBusy("send");
    setPending(text);
    setNotice(null);
    try {
      const c = await sendMessage(request, keys.current.for(action));
      keys.current.done(action);
      setDraft("");
      adopt(c);
    } catch (e) {
      if (!(e instanceof NetworkError)) keys.current.done(action);
      setNotice(supportErrorKey(e));
      if (e instanceof ApiError && e.code === "support_case_closed" && chat) {
        getCase(chat.case_id).then(adopt).catch(() => undefined);
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
      setPending(null);
    }
  }

  async function resolve() {
    if (!chat) return;
    const action = `resolve:${chat.case_id}`;
    busyRef.current = true;
    setBusy("resolve");
    setNotice(null);
    try {
      const c = await resolveCase(chat.case_id, keys.current.for(action));
      keys.current.done(action);
      adopt(c);
      store.remove("support_case");
    } catch (e) {
      if (!(e instanceof NetworkError)) keys.current.done(action);
      setNotice(supportErrorKey(e));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  function startNew() {
    setChat(null);
    setNotice(null);
    setDraft("");
    store.remove("support_case");
    store.remove("support_seen");
  }

  if (!available) return null;

  const messages = chat?.messages ?? [];
  const last = messages.at(-1);
  const resolved = chat?.status === "resolved";
  const outOfTurns = chat?.status === "open" && chat.turns_left <= 0;
  const answered = chat?.status === "open" && busy !== "send" && !!last && last.role !== "customer";
  const waitingForAgent =
    chat?.status === "open" && busy === null && last?.role === "customer" && !chat.ai_available;
  const line = notice ?? (outOfTurns ? "support.error.limit" : waitingForAgent ? "support.error.aiDown" : null);
  const canAsk = !resolved && !outOfTurns;

  return (
    <>
      <button
        type="button"
        className="v-help-fab"
        style={{ "--v-help-lift": `${lift}px` } as CSSProperties}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={show}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H10l-4.5 3.5V16H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
          <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" />
        </svg>
        <span className="v-help-fab__label">{t("support.button")}</span>
        {unread ? (
          <>
            <span className="v-help-fab__dot" aria-hidden="true" />
            <span className="aseed-visually-hidden">{t("support.unread")}</span>
          </>
        ) : null}
      </button>

      <dialog
        ref={dialogRef}
        className="v-help"
        aria-labelledby="v-help-title"
        onClose={hide}
        onClick={(e) => {
          // The panel fills the dialog, so a click on the dialog itself is the backdrop.
          if (e.target === e.currentTarget) hide();
        }}
      >
        <div className="v-help__panel">
          <header className="v-help__head">
            <div className="v-help__titles">
              <h2 id="v-help-title" className="v-help__title">
                {t("support.title")}
              </h2>
              {chat ? <p className="v-help__ref">{t("support.caseRef", { ref: chat.case_ref })}</p> : null}
            </div>
            <button type="button" className="v-help__close" onClick={hide} aria-label={t("support.close")}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </header>

          <div ref={listRef} className="v-help__body">
            <p className="v-help__lead">{t("support.lead")}</p>
            {messages.length === 0 && !pending ? <p className="v-muted v-small">{t("support.hint")}</p> : null}
            {loading && !chat ? (
              <div className="v-center">
                <Spinner label={t("support.loading")} />
              </div>
            ) : null}

            <ol className="v-help__msgs" aria-live="polite">
              {messages.map((m) => (
                <Bubble key={m.seq} role={m.role} body={m.body} />
              ))}
              {pending ? <Bubble role="customer" body={pending} /> : null}
              {busy === "send" ? (
                <li className="v-help__msg v-help__msg--assistant v-help__typing" role="status">
                  <span className="v-help__dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <span className="v-small">{t("support.sending")}</span>
                </li>
              ) : null}
            </ol>

            {answered ? (
              <div className="v-help__answered">
                <button
                  type="button"
                  className="aseed-button v-button-ghost v-wide"
                  disabled={busy !== null}
                  onClick={() => void resolve()}
                >
                  {t("support.resolveAction")}
                </button>
                <p className="v-help__staff">{t("support.staffLine")}</p>
              </div>
            ) : null}

            {resolved ? (
              <div className="v-help__resolved">
                <p>{t("support.resolved")}</p>
                <button type="button" className="aseed-button v-wide" onClick={startNew}>
                  {t("support.newCase")}
                </button>
              </div>
            ) : null}

            {line ? (
              <p className="v-help__notice" role="alert">
                {t(line)}
              </p>
            ) : null}
          </div>

          {canAsk ? (
            <form
              className="v-help__composer"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <label htmlFor="v-help-input" className="aseed-visually-hidden">
                {t("support.placeholder")}
              </label>
              <textarea
                id="v-help-input"
                name="support-message"
                className="v-help__input"
                rows={2}
                maxLength={MAX_MESSAGE}
                value={draft}
                placeholder={t("support.placeholder")}
                disabled={busy === "send"}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit" className="aseed-button v-help__send" disabled={busy !== null || !draft.trim()}>
                {busy === "send" ? <Spinner /> : null}
                {t("support.send")}
              </button>
            </form>
          ) : null}
        </div>
      </dialog>
    </>
  );
}

function Bubble({ role, body }: { role: SupportRole; body: string }) {
  const { t } = useI18n();
  return (
    <li className={`v-help__msg v-help__msg--${role}`}>
      <span className={role === "customer" ? "aseed-visually-hidden" : "v-help__who"}>{t(roleLabelKey(role))}</span>
      <p className="v-help__text">{body}</p>
    </li>
  );
}
